import { IconButton, Link, TableBody, TableCell, TableCellSelectRow, TableRow, Text } from '@ksyos/design-system';
import React, { useState } from 'react';
import type { FundingErrorInformation } from '@common/enums/fundingErrorInformation';
import type { FundingProcess } from '@common/interfaces/api/fundingProcessApi';
import { Dropdown } from '@frontend/components/elements/common';
import { DisplayShortWithTooltip } from '@frontend/components/elements/DisplayShortWithTooltip';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';
import { getFundingErrorInformationOptions } from '../../getFundingErrorInformationOptions';
import { dividerStyling } from './dividerStyling';
import { FinancingTableActivities } from './financingTableActivities/FinancingTableActivities';

type FinancingFlexTableBodyProps = {
    items: FundingProcess[];
    onChangeFundingErrorInformation: (processNumber: number, value?: FundingErrorInformation) => void;
    onSelectRow: (row: FundingProcess) => void;
    selectedRows: FundingProcess[];
};

const dropDownCellStyling = {
    fontWeight: 'normal',
    marginRight: '10px',
    minWidth: '210px',
    overflow: 'hidden',
    paddingRight: '10px',
    paddingX: 0,
    textOverflow: 'ellipsis',
};

const customDropdownStyles = {
    overflow: 'hidden',
    paddingRight: '40px',
    textOverflow: 'ellipsis',
    width: '200px',
};

const expandRowStyles = { display: 'flex', paddingLeft: '8px', paddingY: '15px', width: '185px' };
const showActivityStyles = { alignSelf: 'center', fontWeight: '500', textWrap: 'noWrap' };

export function FinancingFlexTableBody({
    items,
    onChangeFundingErrorInformation,
    onSelectRow,
    selectedRows,
}: FinancingFlexTableBodyProps) {
    const language = getSelectedLanguage();
    const [expandedRows, setExpandedRows] = useState<FundingProcess[]>([]);

    return (
        <TableBody>
            {items.map((item) => {
                const {
                    declarations,
                    fundingResult,
                    id: processId,
                    invoices,
                    processActivities,
                    processNumber,
                    service,
                    singularPatientInvoices,
                    startedDatetime,
                    subTracks,
                } = item;

                const isExpanded = expandedRows.includes(item);
                const isSelected = isRowSelected(item);

                const notFundedReasonTooltip = fundingResult?.notFundedReason && (
                    <DisplayShortWithTooltip
                        maxLength={15}
                        text={language.notFundedReason[fundingResult?.notFundedReason]}
                    />
                );

                const fundingErrorTooltip = fundingResult?.errorCode && (
                    <DisplayShortWithTooltip
                        maxLength={12}
                        text={language.fundingErrorCode[fundingResult?.errorCode]}
                    />
                );

                const processLink = `/processes/new/${processId}`;

                const hasFundingResult =
                    invoices.length > 0 ||
                    declarations.length > 0 ||
                    subTracks.length > 0 ||
                    singularPatientInvoices.length > 0;

                const fundingActivities = {
                    declarations,
                    invoices,
                    processActivities,
                    singularPatientInvoices,
                    subTracks,
                };

                return (
                    <React.Fragment key={processId}>
                        <TableRow>
                            <TableCellSelectRow
                                isSelected={isSelected}
                                onSelect={() => onSelectRow(item)}
                            />
                            <TableCell>
                                <Link
                                    to={processLink}
                                    variant="link"
                                >
                                    {processNumber}
                                </Link>
                            </TableCell>
                            <TableCell>
                                <NullableFormattedDate value={startedDatetime} />
                            </TableCell>
                            <TableCell>{service.code}</TableCell>
                            <TableCell sx={expandRowStyles}>
                                {hasFundingResult ? (
                                    <>
                                        <IconButton
                                            icon={isExpanded ? 'caret-up' : 'caret-down'}
                                            labelText={isExpanded ? 'close' : 'open'}
                                            onClick={() => handleOnExpandRow(item)}
                                            size="small"
                                            variant="ghost"
                                        />
                                        <Text
                                            size={'sm'}
                                            sx={showActivityStyles}
                                        >
                                            {isExpanded
                                                ? language.finance.hideActivities
                                                : language.finance.showActivities}
                                        </Text>
                                    </>
                                ) : null}
                            </TableCell>
                            <TableCell sx={dividerStyling} />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell>{notFundedReasonTooltip}</TableCell>
                            <TableCell>{fundingErrorTooltip}</TableCell>
                            <TableCell sx={dropDownCellStyling}>
                                {fundingResult?.errorCode && (
                                    <Dropdown
                                        customSelectStyles={customDropdownStyles}
                                        onChange={(value) => {
                                            if (value) {
                                                onChangeFundingErrorInformation(
                                                    item.processNumber,
                                                    value as FundingErrorInformation
                                                );
                                            } else {
                                                onChangeFundingErrorInformation(item.processNumber);
                                            }
                                        }}
                                        selectOptions={getFundingErrorInformationOptions(
                                            fundingResult?.fundingErrorInformation || null
                                        )}
                                        value={fundingResult?.fundingErrorInformation}
                                    />
                                )}
                            </TableCell>
                        </TableRow>

                        {hasFundingResult && (
                            <FinancingTableActivities
                                fundingActivities={fundingActivities}
                                isExpanded={isExpanded}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </TableBody>
    );

    function isRowSelected(row: FundingProcess) {
        return selectedRows.some((selectedRow) => selectedRow.id === row.id);
    }

    function handleOnExpandRow(row: FundingProcess) {
        const isExpanded = expandedRows.includes(row);
        if (!isExpanded) {
            return setExpandedRows([...expandedRows, row]);
        }
        setExpandedRows(expandedRows.filter((expandedRow) => expandedRow !== row));
    }
}
